/** Grades page — guardian's read-only list of children's submitted progress reports (11E.8/D5). */

import React, { useState } from 'react';
import { usePortalGrades, getPortalGradePdfUrl, type PortalGrade } from '../api/portal';

const GradeRow: React.FC<{ grade: PortalGrade }> = ({ grade }) => {
  const [opening, setOpening] = useState(false);

  const handleDownload = async () => {
    setOpening(true);
    try {
      const { download_url } = await getPortalGradePdfUrl(grade.id);
      window.open(download_url, '_blank', 'noopener,noreferrer');
    } finally {
      setOpening(false);
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: '0.5rem',
        alignItems: 'center',
        padding: '0.875rem 1rem',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        marginBottom: '0.5rem',
      }}
      data-testid={`candeza-portal-grade-row-${grade.id}`}
    >
      <div>
        <strong style={{ fontSize: '0.9375rem' }}>
          {grade.period_start} – {grade.period_end}
        </strong>
        {grade.student_name && (
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{grade.student_name}</div>
        )}
      </div>
      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
        {grade.instructor_name ?? ''}
      </span>
      <button
        type="button"
        onClick={handleDownload}
        disabled={!grade.pdf_ready || opening}
        data-testid={`candeza-portal-grade-pdf-${grade.id}`}
        style={{
          background: '#6d28d9',
          color: '#fff',
          border: 'none',
          borderRadius: '0.375rem',
          padding: '6px 12px',
          fontSize: '0.75rem',
          fontWeight: 600,
          cursor: grade.pdf_ready ? 'pointer' : 'not-allowed',
          opacity: grade.pdf_ready ? 1 : 0.5,
        }}
      >
        {opening ? 'Opening…' : 'Download PDF'}
      </button>
    </div>
  );
};

const GradesPage: React.FC = () => {
  const { data, isLoading, isError } = usePortalGrades();
  const grades = data?.items ?? [];

  if (isLoading) {
    return <div data-testid="candeza-portal-grades-loading">Loading grades…</div>;
  }
  if (isError) {
    return (
      <div data-testid="candeza-portal-grades-error" style={{ color: '#ef4444' }}>
        Could not load grades.
      </div>
    );
  }

  return (
    <div data-testid="candeza-portal-grades-page">
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1.25rem' }}>
        Grades
      </h1>
      {grades.length === 0 ? (
        <p data-testid="candeza-portal-grades-empty" style={{ color: '#6b7280' }}>
          No progress reports yet.
        </p>
      ) : (
        <div data-testid="candeza-portal-grades-list">
          {grades.map((g) => (
            <GradeRow key={g.id} grade={g} />
          ))}
        </div>
      )}
    </div>
  );
};

export default GradesPage;
